import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import Button from '@dashboard/components/Button';
import Dialog from '@dashboard/components/Dialog';
import { useToast } from '@dashboard/context/ToastContext';
import { Form } from '@dashboard/components/Formik';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Tooltip from '@dashboard/components/Tooltip';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';

const paymentMethodDomainsQueryKey = sellerId => ['seller', sellerId, 'paymentMethodDomains'];

function usePaymentMethods() {
  const { sellerId, token } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: paymentMethodDomainsQueryKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}/payment-method-domains`, token),
  });

  return {
    data,
    isLoading,
    error,
  };
}

export default function PaymentMethodDomains() {
  const { seller, sellerId, token } = useAuth();
  const { data } = usePaymentMethods();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();

  const addPaymentMethodDomainMutation = useMutation({
    mutationFn: async ({ domainName }) => {
      return fetcher(`/sellers/${sellerId}/payment-method-domains`, token, {
        method: 'POST',
        body: JSON.stringify({ domainName }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodDomainsQueryKey(sellerId),
      });
    },
  });

  const validatePaymentMethodDomainMutation = useMutation({
    mutationFn: async ({ domainId }) => {
      const response = await fetcher(
        `/sellers/${sellerId}/payment-method-domains/${domainId}/validate`,
        token,
        {
          method: 'POST',
        },
      );

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodDomainsQueryKey(sellerId),
      });

      emitToast({
        title: 'Domain validated',
      });
    },
    onError: () => {
      emitToast({
        title: 'Domain verification failed',
        description: 'Please make sure you uploaded the domain verification file and try again.',
        type: 'error',
      });
    },
  });

  const removePaymentMethodDomainMutation = useMutation({
    mutationFn: async ({ domainId }) => {
      return fetcher(`/sellers/${sellerId}/payment-method-domains/${domainId}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodDomainsQueryKey(sellerId),
      });

      emitToast({
        title: 'Domain deleted',
      });
    },
    onError: () => {
      emitToast({
        title: 'Failed to delete domain',
        type: 'error',
      });
    },
  });

  return (
    <div>
      <div className="flex justify-between items-end">
        <h3>Domains</h3>

        <Dialog
          title={`Add a domain`}
          description={
            <>
              Add the domain (e.g. yourdomain.com) or a subdomain (e.g. shop.yourdomain.com) where
              you will add checkout pages.
            </>
          }
          cancelButton={null}
          confirmButton={null}
          trigger={
            !checkIsConnectedToStripe(seller) ? (
              <Tooltip label="First connect a Stripe account">
                <Button appearance="primary" disabled>
                  + Add domain
                </Button>
              </Tooltip>
            ) : (
              <Button appearance="primary">+ Add domain</Button>
            )
          }
          size="xl"
        >
          {({ close }) => (
            <Form
              initialValues={{
                domainName: '',
              }}
              fields={[
                {
                  name: 'domainName',
                  type: 'text',
                  label: 'Domain name',
                  required: true,
                },
              ]}
              onSubmit={async values => {
                try {
                  const { data, error } = await addPaymentMethodDomainMutation.mutateAsync(values);

                  if (error) {
                    throw new Error(error);
                  }

                  close();

                  emitToast({
                    title: 'Domain added',
                  });

                  return {
                    data,
                  };
                } catch (error) {
                  return {
                    error,
                  };
                }
              }}
            />
          )}
        </Dialog>
      </div>

      <div className="mt-4 border-t border-gray-100">
        {data?.length === 0 && <div className="text-gray-700 py-4">No domains added yet</div>}

        {data?.map(domain => (
          <div
            className="grid grid-cols-12 border-b last:border-0 border-gray-100 py-2 items-center"
            key={domain._id}
          >
            <div className="col-span-11 font-medium">{domain.domainName}</div>

            <div className="col-span-1 text-right">
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button aria-label="more options" appearance="icon-button" size="narrow">
                    <EllipsisVerticalIcon className="w-4 h-4" />
                  </Button>
                </DropdownMenu.Trigger>

                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    className="origin-top-right absolute right-0 z-20 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
                    side="bottom"
                    align="end"
                  >
                    <DropdownMenu.Item
                      onClick={() =>
                        removePaymentMethodDomainMutation.mutate({
                          domainId: domain._id,
                        })
                      }
                      className="p-2 outline-none hover:bg-gray-100 cursor-pointer"
                    >
                      Delete domain
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
